import React, { useEffect, useMemo, useState } from "react";
import api from "../../../api";
import { useTranslation } from "../../../hooks/useTranslation";
import { useInView } from "../../../hooks/useInView";
import EventCard, { EventCardSkeleton } from "../components/EventCard";
import { citiesMatch } from "../../../utils/places";

const EventsSection = ({ selectedCity }) => {
  const { t } = useTranslation();
  const [ref, isInView] = useInView(0, "250px 0px");
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isInView) return;
    api
      .getAllEvents({ status: ["upcoming", "ongoing"] })
      .then((res) => setEvents(res.data))
      .catch((err) => console.error("Error fetching events for landing page:", err))
      .finally(() => setIsLoading(false));
  }, [isInView]);

  const citiesWithEvents = useMemo(
    () => new Set(events.map((ev) => ev.city).filter(Boolean).map((c) => c.toLowerCase())),
    [events]
  );
  const effectiveCity = selectedCity;

  const filteredEvents = effectiveCity
    ? events.filter((ev) => citiesMatch(ev.city, effectiveCity))
    : events;

  // Hide the whole section if the selected city has no events — the nav
  // dropdown is the single source of truth for which city is showing, so
  // there's no fallback city to substitute and no point showing an empty
  // grid. Before a city is selected, fall back to "does any city have
  // events at all".
  const hasContentForSelection = effectiveCity
    ? citiesWithEvents.has(effectiveCity.toLowerCase())
    : citiesWithEvents.size > 0;
  if (!isLoading && !hasContentForSelection) return null;

  return (
    <section id="events" className="sp-section" style={{ background: "var(--color-surface)", paddingLeft: 32, paddingRight: 32 }}>
      <div style={{ maxWidth: 1240, margin: "0 auto" }}>
        <div className="tag tag-accent">{t.landingEventsKicker}</div>
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: 20,
            flexWrap: "wrap",
            marginTop: 10,
          }}
        >
          <div>
            <h2>{t.landingEventsHeading.replace("{city}", effectiveCity || "")}</h2>
            <p style={{ opacity: 0.75 }}>{t.landingEventsSubtitle}</p>
          </div>
        </div>
        <div
          ref={ref}
          className="sp-cards-grid"
          style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24, marginTop: 30 }}
        >
          {isLoading
            ? Array.from({ length: 3 }).map((_, i) => <EventCardSkeleton key={i} />)
            : filteredEvents.map((ev) => <EventCard key={ev._id} event={ev} />)}
        </div>
        <div style={{ textAlign: "center", marginTop: 30 }}>
          <button className="btn btn-secondary" disabled title="Coming soon">
            {t.landingEventsCtaMore}
          </button>
        </div>
      </div>
    </section>
  );
};

export default EventsSection;
